import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { useAsyncEffect } from '../../support/useAsyncEffect';
import { useDevSettings } from '../../utils/useDevSettings';
import { AppPrincipal } from '../../auth/AppPrincipal';
import { OrganizationInsightsState } from '../OrganizationInsightsState';
import {
    buildAgencyDigest,
    buildCaseloadFigures,
    hasNothingReadable,
    IAgencyCaseloadFigures,
    IUnreadableClient
} from '@innerhive-internal/innerhive-common';
import { buildDemoInsights } from './agencyDemo';

/** The figures are the organization's caseload, or the invented one while the demo setting is on. */
export interface IAgencyInsights extends IAgencyCaseloadFigures {
    hasLoaded: boolean;
    /**
     * The organization has clients but could read none of them, so there is nothing to chart and
     * nothing honest to say in place of it. Neither lens draws in this state: every figure on them
     * is a ratio over the readable set, and over an empty one they read as findings about the book
     * rather than as the absence of one.
     */
    nothingReadable: boolean;
    isDemo: boolean;
    /**
     * Clients left out of every figure above, with the reason and enough to name them.
     *
     * A list rather than a count because the two reasons need different responses: a failed read
     * wants trying again, and a permission gap wants somebody to grant access. A reader given only
     * a number can do neither.
     */
    unreadableClients: IUnreadableClient[];
    /** Reads the caseload again, for the retry a failed read offers. */
    retry: () => Promise<void>;
}

/**
 * The caseload roll-up behind the Insights tab, computed by the server.
 *
 * A caseload too young to chart is still drawn from its own figures: the panels that compare across
 * weeks wait on `historyProgress` and say so, and the rest are real at any age. The invented sample
 * appears only under the admin-only demo setting, which skips reading the caseload at all.
 */
export const useAgencyInsights = (orgId: string): IAgencyInsights => {
    const insightsState = useInstance(OrganizationInsightsState);
    useSubscription(() => insightsState);

    // Checked here as well as at the toggle: the setting stays on the device after the admin who set
    // it signs out, and whoever signs in next must see their own caseload.
    const principal = useInstance(AppPrincipal);
    const isDemo = useDevSettings().showInsightsDemo && principal.isAdmin === true;

    useAsyncEffect(async () => {
        if (isDemo) {
            return;
        }
        await insightsState.load(orgId);
    }, [orgId, insightsState, isDemo]);

    const retry = () => insightsState.reload(orgId);

    if (isDemo) {
        return buildDemoInsights({ now: new Date(), retry });
    }

    // Empty until the caseload arrives, which the tab covers with its loading state.
    const figures = insightsState.getFigures() ?? buildCaseloadFigures(buildAgencyDigest({ findings: [], clients: [], now: new Date() }));

    return {
        ...figures,
        hasLoaded: insightsState.hasLoaded,
        nothingReadable: hasNothingReadable(figures.digest),
        isDemo,
        unreadableClients: insightsState.getUnreadableClients(),
        retry
    };
};
